import { __rest, __assign } from 'tslib';
import React, { useEffect } from 'react';
import RadioGroup from './RadioGroup/RadioGroup.js';
import useHAF from '../../atom/hook/useHAF.js';
import clsx from 'clsx';
import '../../tailwind.css.js';
import useBaseProps from '../../atom/hook/useBaseProps.js';
import { cva } from 'class-variance-authority';

var radioStyles = cva([
    'flex items-center justify-center',
    'bg-white',
    'border-[2px] rounded-full',
    'hover:bg-[#E0EDFB]',
    'active:bg-[#BCDFFD]',
    'focus-visible:ring-4 focus-visible:ring-[#C8DAEB] focus-visible:ring-offset-2',
    'focus-visible:!border-light-text',
], {
    variants: {
        selected: {
            true: "after:bg-blue-brand-500 after:content[''] after:rounded-full after:w-[10px] after:h-[10px] hover:after:bg-[#124C81] hover:border-[#124C81] active:after:bg-[#033B69] active:border-[#033B69] hover:bg-white active:bg-white",
        },
        error: {
            true: "active:bg-[#FACDC7] hover:bg-[#FFF6F5]",
        },
        dense: {
            true: 'w-4 h-4',
            false: 'w-5 h-5',
        },
        disabled: {
            true: 'border-[1.5px] after:!bg-disabled rounded-full bg-gray-cool-100',
        },
    },
    compoundVariants: [
        {
            selected: true,
            dense: true,
            class: 'after:w-[8px] after:h-[8px]',
        },
        {
            selected: false,
            disabled: false,
            error: false,
            class: 'border-light-text',
        },
        {
            disabled: true,
            class: 'border-disabled',
        },
        {
            disabled: false,
            selected: true,
            error: false,
            class: 'border-brand',
        },
        {
            disabled: false,
            error: true,
            selected: false,
            class: 'border-error focus-visible:outline-error',
        },
        {
            disabled: false,
            error: true,
            selected: true,
            class: 'after:bg-error border-error hover:after:bg-compliant-red hover:border-compliant-red active:after:bg-red-900 active:border-red-900 focus-visible:outline-error',
        },
    ],
    defaultVariants: {
        dense: true,
    },
});
var radioLabelStyles = cva(['text-dark-text', 'align-middle', 'select-none', 'whitespace-nowrap', 'truncate'], {
    variants: {
        disabled: {
            true: 'text-disabled-text',
        },
        dense: {
            true: 'text-sm leading-5',
            false: 'text-base leading-[18px]',
        },
    },
    defaultVariants: {
        dense: false,
    },
});
var Radio = function (props) {
    var baseProps = useBaseProps(props);
    var checked = props.checked, className = props.className, children = props.children, label = props.label, dense = props.dense, disabled = props.disabled, onChange = props.onChange, error = props.error, rest = __rest(props, ["checked", "className", "children", "label", "dense", "disabled", "onChange", "error"]);
    var _a = React.useState(!!checked), selected = _a[0], setSelected = _a[1];
    var ref = useHAF().ref;
    var handleChange = function (event) {
        if (disabled)
            return;
        var checkedValue = event.target.checked;
        setSelected(checkedValue);
        checkedValue !== checked && (onChange === null || onChange === void 0 ? void 0 : onChange(event));
    };
    useEffect(function () {
        if (!!checked !== selected) {
            setSelected(!!checked);
        }
    }, [checked]);
    return (React.createElement("div", { className: "flex justify-start items-start ".concat(className) },
        React.createElement("label", { ref: ref, className: clsx('inline-flex items-center', { 'cursor-pointer': !disabled }) },
            React.createElement("span", { "data-testid": 'radio-check', tabIndex: 0, className: radioStyles({ selected: selected, error: !!error, dense: !!dense, disabled: !!disabled }) }),
            (children || label) && (React.createElement("div", { className: 'ml-2' }, children || React.createElement("div", { className: radioLabelStyles({ dense: !!dense, disabled: !!disabled }) }, label))),
            React.createElement("input", __assign({}, rest, baseProps, { type: 'radio', checked: selected, disabled: disabled, onChange: handleChange, className: "hidden" })))));
};
var Radio$1 = Object.assign(Radio, { Group: RadioGroup });

export { Radio$1 as default };
